import React from "react";
import { SectionCard, Textarea, Input, inputCls } from "./ui";
import AiSuggest from "@/components/AiSuggest";
import { useProject } from "@/context/ProjectContext";
import { uid } from "@/lib/uid";
import SupportingMaterials from "./SupportingMaterials";
import { Info, Wrench, Award, BookOpen, Heart, Plus, Trash2 } from "lucide-react";

const ENV = [
  { key: "information", label: "Information", desc: "Clear expectations, data & feedback", icon: Info },
  { key: "tools", label: "Tools & Resources", desc: "Equipment, materials, processes", icon: Wrench },
  { key: "incentives", label: "Incentives", desc: "Rewards & consequences", icon: Award },
];
const IND = [
  { key: "knowledge_skills", label: "Knowledge / Skills", desc: "Capability to perform — triggers ADDIE", icon: BookOpen },
  { key: "motives", label: "Motives", desc: "Willingness & alignment to perform", icon: Heart },
];
const GAP_TYPES = ["Knowledge/Skills", "Information", "Tools & Resources", "Incentives", "Motives"];
const NEED_TYPES = ["Training", "Non-Training"];

function Toggle({ item, checked, onToggle, highlight }) {
  return (
    <button
      data-testid={`cause-${item.key}`}
      onClick={() => onToggle(!checked)}
      className={`text-left w-full border rounded-lg p-4 transition-all flex items-start gap-3 ${
        checked ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300 bg-white"
      }`}
    >
      <div className={`h-9 w-9 rounded-md flex items-center justify-center flex-shrink-0 ${checked ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-500"}`}>
        <item.icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <span className="font-medium text-slate-800 text-sm">{item.label}</span>
          <span className={`h-5 w-5 rounded border flex items-center justify-center ${checked ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`}>
            {checked && <span className="text-white text-xs">✓</span>}
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-0.5">{item.desc}</p>
        {highlight && checked && <p className="text-[11px] text-emerald-600 font-medium mt-1">→ ADDIE Training Engine will activate in Phase 3</p>}
      </div>
    </button>
  );
}

function NeedsTable({ title, desc, items, onAdd, onUpd, onDel, testid }) {
  return (
    <SectionCard
      title={title}
      desc={desc}
      right={<button data-testid={`add-${testid}-btn`} onClick={onAdd} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add</button>}
      testid={`phase2-${testid}`}
    >
      <div className="space-y-2">
        {items.length === 0 && <p className="text-sm text-slate-400">No items yet.</p>}
        {items.map((n, i) => (
          <div key={n.id || i} className="grid sm:grid-cols-[1fr_1.3fr_1.3fr_auto] gap-2 items-start" data-testid={`${testid}-row-${i}`}>
            <Input value={n.topic} onChange={(e) => onUpd(i, "topic", e.target.value)} placeholder="Topic" data-testid={`${testid}-topic-${i}`} />
            <Input value={n.need} onChange={(e) => onUpd(i, "need", e.target.value)} placeholder="Need" />
            <Input value={n.evidence} onChange={(e) => onUpd(i, "evidence", e.target.value)} placeholder="Evidence" />
            <button onClick={() => onDel(i)} className="text-slate-300 hover:text-red-500 justify-self-center mt-2"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

export default function Phase2() {
  const { phaseData: pd, setPhase } = useProject();
  const p = pd.phase2 || { environmental: {}, individual: {}, notes: "" };
  const setEnv = (k, v) => setPhase("phase2", { ...p, environmental: { ...p.environmental, [k]: v } });
  const setInd = (k, v) => setPhase("phase2", { ...p, individual: { ...p.individual, [k]: v } });

  const mkNeeds = (field) => {
    const items = p[field] || [];
    return {
      items,
      add: () => setPhase("phase2", { ...p, [field]: [...items, { id: uid(), topic: "", need: "", evidence: "" }] }),
      upd: (i, k, v) => { const a = [...items]; a[i] = { ...a[i], [k]: v }; setPhase("phase2", { ...p, [field]: a }); },
      del: (i) => setPhase("phase2", { ...p, [field]: items.filter((_, idx) => idx !== i) }),
    };
  };
  const trainNeeds = mkNeeds("training_needs");
  const ntNeeds = mkNeeds("non_training_needs");

  const rootCauses = p.root_causes || [];
  const setRoot = (r) => setPhase("phase2", { ...p, root_causes: r });
  const addRoot = () => setRoot([...rootCauses, { id: uid(), need: "", evidence: "", gap_type: "Knowledge/Skills", need_type: "Training" }]);
  const updRoot = (i, k, v) => { const a = [...rootCauses]; a[i] = { ...a[i], [k]: v }; setRoot(a); };
  const delRoot = (i) => setRoot(rootCauses.filter((_, idx) => idx !== i));

  const applyAi = (suggestions) => {
    const env = { ...p.environmental };
    const ind = { ...p.individual };
    const map = { Information: ["env", "information"], Tools: ["env", "tools"], Incentives: ["env", "incentives"], "Knowledge/Skills": ["ind", "knowledge_skills"], Motives: ["ind", "motives"] };
    const lines = [];
    suggestions.forEach((s) => {
      const t = map[s.category];
      if (t) { if (t[0] === "env") env[t[1]] = true; else ind[t[1]] = true; }
      lines.push(`• [${s.category}] ${s.cause}`);
    });
    setPhase("phase2", { ...p, environmental: env, individual: ind, notes: (p.notes ? p.notes + "\n" : "") + lines.join("\n") });
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <SectionCard
        title="Environmental Factors"
        desc="Workplace conditions — often the cheapest, fastest causes to fix (Gilbert's BEM)."
        right={<AiSuggest kind="causes" context={{ gap: pd.phase1 }} onResults={applyAi} label="AI Diagnose Causes" />}
        testid="phase2-environmental"
      >
        <div className="grid sm:grid-cols-3 gap-3">
          {ENV.map((it) => <Toggle key={it.key} item={it} checked={!!p.environmental?.[it.key]} onToggle={(v) => setEnv(it.key, v)} />)}
        </div>
      </SectionCard>

      <SectionCard title="Individual Factors" desc="Person-level capability & motivation." testid="phase2-individual">
        <div className="grid sm:grid-cols-2 gap-3">
          {IND.map((it) => <Toggle key={it.key} item={it} checked={!!p.individual?.[it.key]} onToggle={(v) => setInd(it.key, v)} highlight={it.key === "knowledge_skills"} />)}
        </div>
      </SectionCard>

      <SectionCard title="Diagnostic Notes" desc="Document evidence, observations and root-cause reasoning in depth." testid="phase2-notes">
        <textarea
          data-testid="cause-notes-input"
          value={p.notes || ""}
          onChange={(e) => setPhase("phase2", { ...p, notes: e.target.value })}
          placeholder="Evidence, observations and root-cause reasoning…"
          className="w-full border border-slate-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all text-sm resize-y"
          style={{ minHeight: "33vh" }}
        />
      </SectionCard>

      <NeedsTable title="Training Needs" desc="Capability gaps that warrant a learning solution (topic, the need, supporting evidence)." items={trainNeeds.items} onAdd={trainNeeds.add} onUpd={trainNeeds.upd} onDel={trainNeeds.del} testid="training-needs" />
      <NeedsTable title="Non-Training Needs" desc="Environmental / motivational gaps best solved without training." items={ntNeeds.items} onAdd={ntNeeds.add} onUpd={ntNeeds.upd} onDel={ntNeeds.del} testid="non-training-needs" />

      <SectionCard
        title="Root Cause Analysis"
        desc="Classify each performance need by the type of gap and whether it warrants a training or non-training solution (Data Workbook)."
        right={<button data-testid="add-root-cause-btn" onClick={addRoot} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add</button>}
        testid="phase2-root-cause"
      >
        <div className="space-y-2">
          {rootCauses.length === 0 && <p className="text-sm text-slate-400">No root causes documented yet.</p>}
          {rootCauses.map((r, i) => (
            <div key={r.id || i} className="grid sm:grid-cols-[1.2fr_1.2fr_1fr_1fr_auto] gap-2 items-center" data-testid={`root-cause-row-${i}`}>
              <Input value={r.need} onChange={(e) => updRoot(i, "need", e.target.value)} placeholder="Need / performance gap" data-testid={`root-cause-need-${i}`} />
              <Input value={r.evidence} onChange={(e) => updRoot(i, "evidence", e.target.value)} placeholder="Evidence" />
              <select value={r.gap_type} onChange={(e) => updRoot(i, "gap_type", e.target.value)} className={inputCls} data-testid={`root-cause-gap-${i}`}>
                {GAP_TYPES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
              <select value={r.need_type} onChange={(e) => updRoot(i, "need_type", e.target.value)} className={inputCls} data-testid={`root-cause-needtype-${i}`}>
                {NEED_TYPES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <button onClick={() => delRoot(i)} className="text-slate-300 hover:text-red-500 justify-self-center"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </SectionCard>

      <SupportingMaterials phaseKey="phase2" />
    </div>
  );
}
